import { useState, useEffect } from "react";
import {
  adminGetDashboard,
  adminGetUsers,
  adminGetSubscriptionReport,
  adminGetApplicationReport,
  adminGetDisbursementReport,
  adminGetRevenueReport,
  adminGetCollectionReport,
  adminGetAgentReport
} from "@/api";
import {
  Users, FileText, CreditCard, UserCog, TrendingUp, Clock,
  BarChart3, PieChart as PieIcon, Activity, IndianRupee,
  ArrowUpRight, ArrowDownRight, Wallet, CircleDollarSign,
  CheckCircle2, AlertTriangle, XCircle, ShieldCheck
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  AreaChart, Area, LineChart, Line, RadialBarChart, RadialBar
} from 'recharts';

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [collections, setCollections] = useState(null);
  const [agentsReport, setAgentsReport] = useState([]);
  const [appReport, setAppReport] = useState(null);
  const [disbReport, setDisbReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const results = await Promise.allSettled([
          adminGetDashboard(),
          adminGetUsers({}),
          adminGetSubscriptionReport(),
          adminGetApplicationReport(),
          adminGetDisbursementReport(),
          adminGetRevenueReport(),
          adminGetCollectionReport(),
          adminGetAgentReport()
        ]);

        const [dashRes, usersRes, subRes, appRes, disbRes, revRes, colRes, agentRes] = results;

        if (dashRes.status === 'fulfilled') setStats(dashRes.value.data.stats);
        if (usersRes.status === 'fulfilled') setUsersList(usersRes.value.data.users || []);
        if (subRes.status === 'fulfilled') setSubscriptions(subRes.value.data.subscriptions || []);
        if (appRes.status === 'fulfilled') setAppReport(appRes.value.data);
        if (disbRes.status === 'fulfilled') setDisbReport(disbRes.value.data);
        if (revRes.status === 'fulfilled') setRevenue(revRes.value.data);
        if (colRes.status === 'fulfilled') setCollections(colRes.value.data);
        if (agentRes.status === 'fulfilled') setAgentsReport(agentRes.value.data.agents || []);
      } catch (e) {
        console.error('[AdminOverview] Error loading data:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // --- Chart data derivations ---

  // Application status donut
  const appStatusData = appReport ? [
    { name: 'Pending', value: appReport.pending || 0, color: '#F59E0B' },
    { name: 'Approved', value: appReport.approved || 0, color: '#10B981' },
    { name: 'Rejected', value: appReport.rejected || 0, color: '#EF4444' },
    { name: 'Disbursed', value: appReport.disbursed || 0, color: '#3B82F6' },
  ].filter(d => d.value > 0) : [];

  // Disbursement status donut
  const disbStatusData = disbReport ? [
    { name: 'Pending', value: disbReport.pending || 0, color: '#F59E0B' },
    { name: 'Approved', value: disbReport.approved || 0, color: '#8B5CF6' },
    { name: 'Transferred', value: disbReport.transferred || 0, color: '#10B981' },
  ].filter(d => d.value > 0) : [];

  // KYC breakdown from real users list
  const kycData = (() => {
    const verified = usersList.filter(u => u.kyc_status === 'verified' || u.kyc_status === 'completed').length;
    const pending = usersList.filter(u => u.kyc_status === 'pending' || !u.kyc_status).length;
    const rejected = usersList.filter(u => u.kyc_status === 'rejected').length;
    const panDone = usersList.filter(u => u.pan_verified).length;
    const aadhaarDone = usersList.filter(u => u.aadhaar_verified).length;
    return {
      bars: [
        { name: 'Verified', count: verified, fill: '#10B981' },
        { name: 'Pending', count: pending, fill: '#F59E0B' },
        { name: 'Rejected', count: rejected, fill: '#EF4444' },
      ],
      panDone,
      aadhaarDone,
      total: usersList.length
    };
  })();

  // Revenue trend (monthly from subscriptions)
  const revenueTrend = (() => {
    const monthMap = {};
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      monthMap[key] = { month: months[d.getMonth()], revenue: 0, count: 0 };
    }
    subscriptions.forEach(s => {
      if (s.activated_at) {
        const key = s.activated_at.substring(0, 7);
        if (monthMap[key]) {
          monthMap[key].revenue += Number(s.amount || 0);
          monthMap[key].count += 1;
        }
      }
    });
    return Object.values(monthMap);
  })();

  // Collections breakdown for chart
  const collectionsChartData = collections ? [
    { name: 'Collected', value: collections.collected || 0, color: '#10B981' },
    { name: 'Pending', value: collections.pending || 0, color: '#F59E0B' },
    { name: 'Overdue', value: collections.overdue || 0, color: '#EF4444' },
  ].filter(d => d.value > 0) : [];

  // Agent performance (enhanced with commissions)
  const agentPerformance = agentsReport
    .map(a => ({
      name: (a.name || 'Unknown').split(' ')[0],
      referrals: a.total_customers || 0,
      applications: a.total_applications || 0,
      approved: a.approved_applications || 0,
      conversion: a.conversion_rate || 0,
      commission: a.total_commission || 0,
    }))
    .sort((a, b) => b.referrals - a.referrals)
    .slice(0, 6);

  // Revenue breakdown for horizontal bar
  const revenueBreakdown = revenue ? [
    { name: 'Subscriptions', amount: revenue.subscription_revenue || 0, fill: '#3B82F6' },
    { name: 'Interest', amount: revenue.interest_income || 0, fill: '#8B5CF6' },
    { name: 'Repayments', amount: revenue.repayments_collected || 0, fill: '#10B981' },
  ] : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl p-3 shadow-lg">
          <p className="text-xs font-bold text-[#0A2540] mb-1">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} className="text-xs text-[#6B7280]">
              <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: entry.color || entry.fill }}></span>
              {entry.name}: <span className="font-semibold text-[#0A2540]">
                {typeof entry.value === 'number' && entry.value >= 100 ? `₹${entry.value.toLocaleString('en-IN')}` : entry.value}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const formatCurrency = (val) => {
    if (val >= 100000) return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 border-3 border-[#1565C0]/20 border-t-[#1565C0] rounded-full animate-spin" style={{ borderWidth: '3px' }} />
        <p className="text-sm text-[#6B7280]">Loading dashboard...</p>
      </div>
    </div>
  );

  const s = stats || {};

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-overview">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-black text-[#0A2540] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard Overview</h2>
        <p className="text-sm text-[#6B7280] mt-1">Real-time platform metrics and performance</p>
      </div>

      {/* ===== STATS CARDS (6 cards) ===== */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          {
            icon: Users, label: "Customers", value: s.total_customers || 0,
            color: "#3B82F6", bg: "bg-blue-50",
            sub: `${s.active_customers || 0} active`
          },
          {
            icon: FileText, label: "Applications", value: s.total_applications || 0,
            color: "#8B5CF6", bg: "bg-violet-50",
            sub: s.pending_applications > 0 ? `${s.pending_applications} pending` : 'All reviewed'
          },
          {
            icon: UserCog, label: "Agents", value: s.total_agents || 0,
            color: "#F59E0B", bg: "bg-amber-50",
            sub: `${s.active_agents || 0} active`
          },
          {
            icon: IndianRupee, label: "Subscription Rev.", value: formatCurrency(s.subscription_revenue || 0),
            color: "#10B981", bg: "bg-emerald-50",
            sub: `${subscriptions.length} paid`
          },
          {
            icon: Wallet, label: "Disbursed", value: formatCurrency(s.total_disbursed_amount || 0),
            color: "#0EA5E9", bg: "bg-sky-50",
            sub: `${s.total_disbursements || 0} total`
          },
          {
            icon: CircleDollarSign, label: "Collections", value: formatCurrency(collections?.collected || 0),
            color: "#EC4899", bg: "bg-pink-50",
            sub: collections ? `${collections.collection_rate}% rate` : '0%'
          },
        ].map((card, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
            <div className="flex items-center justify-between mb-2.5">
              <div className={`${card.bg} p-2 rounded-xl`}>
                <card.icon className="h-4 w-4" style={{ color: card.color }} strokeWidth={2} />
              </div>
            </div>
            <p className="text-2xl font-black text-[#0A2540] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{card.value}</p>
            <p className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold mt-0.5">{card.label}</p>
            <p className="text-[10px] text-[#9CA3AF] mt-1">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* ===== ROW 1: Revenue Trend + Application Status ===== */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Revenue Trend */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#1565C0]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue Trend</h3>
            </div>
            <span className="text-xs text-[#6B7280] bg-gray-50 px-2.5 py-1 rounded-lg">Last 6 months</span>
          </div>
          <div className="h-[220px]">
            {revenueTrend.some(r => r.revenue > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueTrend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1565C0" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#1565C0" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="revenue" stroke="#1565C0" strokeWidth={2.5} fill="url(#revenueGradient)" name="Revenue" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-[#9CA3AF]">
                <Activity className="h-10 w-10 mb-2 opacity-30" />
                <p className="text-sm font-medium">No revenue data yet</p>
                <p className="text-xs mt-1">Revenue will appear after successful subscriptions</p>
              </div>
            )}
          </div>
        </div>

        {/* Application Status Donut */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <PieIcon className="h-5 w-5 text-[#8B5CF6]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Application Status</h3>
            </div>
            <span className="text-xs text-[#6B7280] bg-gray-50 px-2.5 py-1 rounded-lg">{appReport?.total || 0} total</span>
          </div>
          <div className="h-[220px] flex items-center">
            {appStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={appStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" strokeWidth={0}>
                    {appStatusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={({ active, payload }) => active && payload?.length ? (
                    <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl p-3 shadow-lg">
                      <p className="text-xs font-bold" style={{ color: payload[0].payload.color }}>{payload[0].name}: {payload[0].value}</p>
                    </div>
                  ) : null} />
                  <Legend formatter={(value) => <span className="text-xs font-semibold text-[#6B7280]">{value}</span>} iconType="circle" iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-[#9CA3AF]">
                <FileText className="h-10 w-10 mb-2 opacity-30" />
                <p className="text-sm font-medium">No applications yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== ROW 2: KYC Verification + Collections ===== */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* KYC Verification */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#10B981]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>KYC Verification</h3>
            </div>
            <span className="text-xs text-[#6B7280] bg-gray-50 px-2.5 py-1 rounded-lg">{kycData.total} users</span>
          </div>
          {/* KYC stats mini cards */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-emerald-50 rounded-xl p-2.5 text-center">
              <p className="text-lg font-black text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{kycData.panDone}</p>
              <p className="text-[9px] uppercase tracking-wider text-emerald-600/70 font-bold">PAN Done</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-2.5 text-center">
              <p className="text-lg font-black text-blue-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{kycData.aadhaarDone}</p>
              <p className="text-[9px] uppercase tracking-wider text-blue-600/70 font-bold">Aadhaar Done</p>
            </div>
            <div className="bg-violet-50 rounded-xl p-2.5 text-center">
              <p className="text-lg font-black text-violet-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.kyc_completed || 0}</p>
              <p className="text-[9px] uppercase tracking-wider text-violet-600/70 font-bold">Full KYC</p>
            </div>
          </div>
          <div className="h-[160px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kycData.bars} margin={{ top: 5, right: 10, left: -10, bottom: 0 }} barSize={36}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Users" radius={[8, 8, 0, 0]}>
                  {kycData.bars.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Collections Overview */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CircleDollarSign className="h-5 w-5 text-[#EC4899]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Collections</h3>
            </div>
            <span className="text-xs text-[#6B7280] bg-gray-50 px-2.5 py-1 rounded-lg">
              {collections?.total_installments || 0} installments
            </span>
          </div>
          {/* Collection stats */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-emerald-50 rounded-xl p-2.5 text-center">
              <div className="flex items-center justify-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                <p className="text-sm font-black text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{collections?.paid_count || 0}</p>
              </div>
              <p className="text-[9px] uppercase tracking-wider text-emerald-600/70 font-bold">Paid</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-2.5 text-center">
              <div className="flex items-center justify-center gap-1">
                <Clock className="h-3 w-3 text-amber-600" />
                <p className="text-sm font-black text-amber-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{collections?.pending_count || 0}</p>
              </div>
              <p className="text-[9px] uppercase tracking-wider text-amber-600/70 font-bold">Pending</p>
            </div>
            <div className="bg-red-50 rounded-xl p-2.5 text-center">
              <div className="flex items-center justify-center gap-1">
                <AlertTriangle className="h-3 w-3 text-red-600" />
                <p className="text-sm font-black text-red-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{collections?.overdue_count || 0}</p>
              </div>
              <p className="text-[9px] uppercase tracking-wider text-red-600/70 font-bold">Overdue</p>
            </div>
          </div>
          {/* Collection amounts */}
          <div className="space-y-2.5">
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[#6B7280]">Total Due</span>
                <span className="font-bold text-[#0A2540]">₹{(collections?.total_due || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-gray-300 h-2 rounded-full" style={{ width: '100%' }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-emerald-600">Collected</span>
                <span className="font-bold text-emerald-600">₹{(collections?.collected || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${collections?.collection_rate || 0}%` }}></div>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-amber-600">Pending</span>
                <span className="font-bold text-amber-600">₹{(collections?.pending || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-amber-400 h-2 rounded-full transition-all" style={{ width: `${collections?.total_due > 0 ? (collections.pending / collections.total_due * 100) : 0}%` }}></div>
              </div>
            </div>
            {(collections?.overdue || 0) > 0 && (
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-red-600">Overdue</span>
                  <span className="font-bold text-red-600">₹{(collections?.overdue || 0).toLocaleString('en-IN')}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="bg-red-500 h-2 rounded-full transition-all" style={{ width: `${collections?.total_due > 0 ? (collections.overdue / collections.total_due * 100) : 0}%` }}></div>
                </div>
              </div>
            )}
          </div>
          {/* Collection rate badge */}
          <div className="mt-4 flex items-center justify-center">
            <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl px-4 py-2 text-center">
              <p className="text-2xl font-black text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{collections?.collection_rate || 0}%</p>
              <p className="text-[9px] uppercase tracking-wider text-[#6B7280] font-bold">Collection Rate</p>
            </div>
          </div>
        </div>
      </div>

      {/* ===== ROW 3: Revenue Breakdown + Agent Performance ===== */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Revenue Breakdown */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-[#10B981]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue Breakdown</h3>
            </div>
            {revenue && (
              <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-lg">
                Net: {formatCurrency(revenue.net_revenue || 0)}
              </span>
            )}
          </div>
          {revenue ? (
            <div className="space-y-4">
              {/* Revenue cards */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Subscription Revenue', value: revenue.subscription_revenue || 0, color: '#3B82F6', icon: CreditCard },
                  { label: 'Interest Income', value: revenue.interest_income || 0, color: '#8B5CF6', icon: TrendingUp },
                  { label: 'Repayments Collected', value: revenue.repayments_collected || 0, color: '#10B981', icon: CheckCircle2 },
                  { label: 'Total Disbursed', value: revenue.total_disbursed || 0, color: '#EF4444', icon: ArrowUpRight },
                ].map((item, i) => (
                  <div key={i} className="border border-gray-100 rounded-xl p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <item.icon className="h-3.5 w-3.5" style={{ color: item.color }} />
                      <p className="text-[10px] uppercase tracking-wider text-[#6B7280] font-bold">{item.label}</p>
                    </div>
                    <p className="text-lg font-black text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      ₹{item.value.toLocaleString('en-IN')}
                    </p>
                  </div>
                ))}
              </div>
              {/* Net revenue highlight */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 text-center border border-emerald-100">
                <p className="text-[10px] uppercase tracking-wider text-emerald-600/70 font-bold mb-1">Net Revenue</p>
                <p className="text-3xl font-black text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  ₹{(revenue.net_revenue || 0).toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-emerald-600/60 mt-1">Subscriptions + Repayments - Disbursed</p>
              </div>
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-[#9CA3AF] text-sm">Loading revenue data...</div>
          )}
        </div>

        {/* Agent Performance */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#F59E0B]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Agent Performance</h3>
            </div>
            <span className="text-xs text-[#6B7280] bg-gray-50 px-2.5 py-1 rounded-lg">{agentsReport.length} agents</span>
          </div>
          {agentPerformance.length > 0 ? (
            <div className="space-y-3">
              {agentPerformance.map((agent, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 border border-gray-100 rounded-xl hover:bg-gray-50/50 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {agent.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0A2540] truncate">{agent.name}</p>
                    <div className="flex gap-3 mt-0.5">
                      <span className="text-[10px] text-[#6B7280]">
                        <span className="font-bold text-blue-600">{agent.referrals}</span> customers
                      </span>
                      <span className="text-[10px] text-[#6B7280]">
                        <span className="font-bold text-violet-600">{agent.applications}</span> apps
                      </span>
                      <span className="text-[10px] text-[#6B7280]">
                        <span className="font-bold text-emerald-600">{agent.conversion}%</span> conv.
                      </span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-black text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      ₹{agent.commission.toLocaleString('en-IN')}
                    </p>
                    <p className="text-[9px] text-[#9CA3AF] uppercase tracking-wider">commission</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[200px] flex flex-col items-center justify-center text-[#9CA3AF]">
              <UserCog className="h-10 w-10 mb-2 opacity-30" />
              <p className="text-sm font-medium">No agent data</p>
            </div>
          )}
        </div>
      </div>

      {/* ===== ROW 4: Disbursement Pipeline ===== */}
      {(disbReport?.total || 0) > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-[#3B82F6]" />
              <h3 className="text-lg font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>Disbursement Pipeline</h3>
            </div>
            <span className="text-xs text-[#6B7280] bg-gray-50 px-2.5 py-1 rounded-lg">{disbReport.total} total</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: 'Total', value: disbReport.total, color: '#3B82F6', bg: 'bg-blue-50' },
              { label: 'Pending', value: disbReport.pending, color: '#F59E0B', bg: 'bg-amber-50' },
              { label: 'Approved', value: disbReport.approved, color: '#8B5CF6', bg: 'bg-violet-50' },
              { label: 'Transferred', value: disbReport.transferred, color: '#10B981', bg: 'bg-emerald-50' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="relative mx-auto w-16 h-16 mb-2">
                  <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#F3F4F6" strokeWidth="3" />
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke={item.color} strokeWidth="3" strokeLinecap="round"
                      strokeDasharray={`${disbReport.total > 0 ? (item.value / disbReport.total) * 100 : 0}, 100`} />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-sm font-black text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.value}</span>
                </div>
                <p className="text-xs font-semibold" style={{ color: item.color }}>{item.label}</p>
              </div>
            ))}
          </div>
          {disbReport.total_disbursed > 0 && (
            <div className="mt-4 text-center">
              <p className="text-xs text-[#6B7280]">Total Disbursed Amount: <span className="font-bold text-[#0A2540]">₹{disbReport.total_disbursed.toLocaleString('en-IN')}</span></p>
            </div>
          )}
        </div>
      )}

      {/* ===== ROW 5: Quick Actions ===== */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Actions</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              label: "Review KYC",
              icon: ShieldCheck,
              desc: `${usersList.filter(u => u.kyc_status === 'pending' || !u.kyc_status).length} pending`,
              href: "/admin/users",
              color: "#10B981"
            },
            {
              label: "Process Applications",
              icon: FileText,
              desc: `${s.pending_applications || 0} pending review`,
              href: "/admin/applications",
              color: "#8B5CF6"
            },
            {
              label: "Manage Disbursements",
              icon: CreditCard,
              desc: `${disbReport?.pending || 0} pending approval`,
              href: "/admin/disbursements",
              color: "#3B82F6"
            },
            {
              label: "Overdue Collections",
              icon: AlertTriangle,
              desc: `${collections?.overdue_count || 0} overdue`,
              href: "/admin/reports",
              color: "#EF4444"
            },
          ].map((item, i) => (
            <a key={i} href={item.href} className="flex items-center gap-3 p-4 border border-gray-200 rounded-xl hover:border-[#1565C0] hover:bg-blue-50/50 transition-all group">
              <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center group-hover:bg-white transition-colors">
                <item.icon className="h-5 w-5" style={{ color: item.color }} strokeWidth={2} />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0A2540]">{item.label}</p>
                <p className="text-xs text-[#6B7280] mt-0.5">{item.desc}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
